/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon, CheckIcon } from '@coursera/cds-icons';

import { useAiReviewSummary } from 'bundles/unified-description-page-common/components/modules/reviews/useAiReview';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';

import _t from 'i18n!nls/xdp';

const styles = {
  container: css`
    display: flex;
    max-width: 640px;
    gap: var(--cds-spacing-200);
    flex-direction: column;
    margin-bottom: var(--cds-spacing-600);

    > * {
      display: inline-flex;
    }
  `,
  aiGenerateIcon: css`
    margin-right: var(--cds-spacing-50);
  `,
  sentimentGrid: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-100) var(--cds-spacing-200);
    flex-wrap: wrap;
  `,
  sentimentGridItem: css`
    display: inline-block;
    vertical-align: middle;
  `,
  sentimentGridItemIcon: css`
    vertical-align: middle;
    margin-right: var(--cds-spacing-50);
  `,
  sentimentGridItemText: css`
    vertical-align: middle;
  `,
};

const AIReview = () => {
  const pageData = useCoursePageData();
  const {
    data: aiReviewSummary,
    loading: aiReviewSummaryLoading,
    error: aiReviewSummaryError,
  } = useAiReviewSummary(pageData?.id ?? '');

  if (aiReviewSummaryLoading || aiReviewSummaryError || !aiReviewSummary) {
    return null;
  }

  const summary =
    aiReviewSummary?.ExternallyAccessibleNostosV1Resource?.getAllProperties?.elements?.[0]?.content.review;
  const sentiment =
    aiReviewSummary?.ExternallyAccessibleNostosV1Resource?.getAllProperties?.elements?.[0]?.content.sentiment;
  const jsonSummarySentiments = JSON.parse(sentiment ?? null);

  if (!summary) {
    return null;
  }

  return (
    <div css={styles.container}>
      <Typography2 variant="subtitleMedium" component="h2">
        <AIGenerateIcon css={styles.aiGenerateIcon} />
        {_t('AI snapshot of learner reviews')}
      </Typography2>
      <Typography2 variant="bodyPrimary" component="p">
        {summary}
      </Typography2>
      {jsonSummarySentiments && (
        <div css={styles.sentimentGrid} role="list">
          {Object.entries(jsonSummarySentiments).map(
            ([sent, value]) =>
              value === 'positive' && (
                <div key={sentiment} css={styles.sentimentGridItem} role="listitem">
                  <CheckIcon
                    css={styles.sentimentGridItemIcon}
                    color="success"
                    aria-hidden="false"
                    aria-label={_t('Positive sentiment icon')}
                  />
                  <Typography2 variant="actionSecondary" component="span" css={styles.sentimentGridItemText}>
                    {sent}
                  </Typography2>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

export default AIReview;
